import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import DataGrid, { Column, FilterRow, HeaderFilter, Paging, Pager, Selection } from 'devextreme-react/data-grid';
import moment from 'moment';
import { getAllCustomerSelectList, getAllDiscountVoucherCodeByDiscountVouId, updateDiscountVoucher } from 'pages/Componets/API_Modal/DiscountAPI/DiscountVoucherAPIModal';
import { Select, message } from 'antd';
import { getAllStoreForSelect } from 'pages/Componets/API_Modal/StoreMasterAPIModal';
import notistack from 'pages/Core/Notistack';

type DiscountVoucher = {
    id: string | number;
    amount: number;
    minInvoiceAmt: number;
    isLifetime: boolean;
    fromDate: string;
    toDate: string;
    isActive: boolean;
    isAllStore: boolean;
    voucherType: string;
    withGst: boolean;
    isConsumerOfferApplicable: boolean;
};

type ViewModalWithTableProps = {
    selectedId: DiscountVoucher | null;
    show: boolean;
    onClose: () => void;
};

type Type = {
    text: any;
    value: any;
};

const ViewModalWithTable: React.FC<ViewModalWithTableProps> = ({ selectedId, show, onClose }) => {
    const dataGridRef = useRef<any>(null);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    const [storeAllData, setStoreAllData] = useState<Type[]>([]);
    const [customerAllData, setCustomerAllData] = useState<Type[]>([]);
    const [selectedStore, setSelectedStore] = useState<number | null>(null);
    const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null);
    const [assignStoreLoading, setAssignStoreLoading] = useState(false);
    const [assignCustomerLoading, setAssignCustomerLoading] = useState(false);

    useEffect(() => {
        if (show && selectedId !== null) {
            loadData(selectedId.id);
            if (!selectedId.isAllStore) {
                fetchStores();
            }
        }
    }, [show, selectedId]);

    const loadData = async (id: string | number) => {
        try {
            setLoading(true);
            const response = await getAllDiscountVoucherCodeByDiscountVouId(id);
            setData(response?.data || []);
        } catch (err) {
            console.error('Error loading data:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            const [storeRes, customerRes] = await Promise.all([
                getAllStoreForSelect(),
                getAllCustomerSelectList(),
            ]);

            setStoreAllData(storeRes?.data || []);
            setCustomerAllData(customerRes?.data || []);
        } catch (err) {
            console.error("Failed to fetch stores", err);
        }
    };

    const handleAssignToStore = async () => {
        if (!selectedStore) {
            message.error("Please select a store.");
            return;
        }
        if (selectedRows.length === 0) {
            message.error("Please select at least one voucher.");
            return;
        }

        try {
            setAssignStoreLoading(true);

            const payload = {
                discountVoucherCodeIds: selectedRows,
                storeId: Number(selectedStore)
            };

            let response = await updateDiscountVoucher(payload);
            notistack.notifyFromApi(response);

            if (response?.status === 200) {
                setSelectedCustomer(null);
                setSelectedStore(null);
                setSelectedRows([]);
                handleClose();
                dataGridRef?.current?.instance?.refresh();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setAssignStoreLoading(false);
        }
    };

    const handleAssignToCustomer = async () => {
        if (!selectedCustomer) {
            message.error("Please select a customer.");
            return;
        }
        if (selectedRows.length === 0) {
            message.error("Please select at least one voucher.");
            return;
        }

        try {
            setAssignCustomerLoading(true);

            const payload = {
                discountVoucherCodeIds: selectedRows,
                customerId: Number(selectedCustomer)
            };

            let response = await updateDiscountVoucher(payload);
            notistack.notifyFromApi(response);

            if (response?.status === 200) {
                setSelectedCustomer(null);
                setSelectedStore(null);
                setSelectedRows([]);
                handleClose();
                dataGridRef?.current?.instance?.refresh();
            }
        } catch (err) {
            console.error(err);
        } finally {
            setAssignCustomerLoading(false);
        }
    };

    const resetState = () => {
        setSelectedStore(null);
        setSelectedCustomer(null);
        setSelectedRows([]);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    return (
        <>

            <Modal show={show} onHide={handleClose} size="lg" centered backdrop='static'>
                <Modal.Header closeButton>
                    <Modal.Title>Discount Voucher View Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>

                    <div className='row'>
                        {selectedId && !selectedId.isAllStore && (
                            <div className="col-md-6 d-flex justify-content-between align-items-center mb-3 gx-2">

                                <div className="col-md-6">
                                    <Select
                                        placeholder="Select Store"
                                        className="w-100"
                                        value={selectedStore}
                                        onChange={(value) => setSelectedStore(value)}
                                        options={storeAllData.map((store: any) => ({
                                            label: store.text,
                                            value: store.value,
                                        }))}
                                        showSearch
                                        allowClear
                                        optionFilterProp="label"
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    />
                                </div>
                                <div className="col-md-5">
                                    <button
                                        className="submit-btn w-auto"
                                        onClick={handleAssignToStore}
                                        disabled={selectedRows.length === 0 || !selectedStore || assignStoreLoading}
                                    >
                                        {assignStoreLoading ? "Assigning..." : "Assign To Store"}
                                    </button>
                                </div>
                            </div>
                        )}
                        {selectedId && !selectedId.isAllStore && (
                            <div className="col-md-6 d-flex justify-content-between align-items-center mb-3 gx-2">
                                <div className="col-md-6">
                                    <Select
                                        placeholder="Select Customer"
                                        className="w-100"
                                        value={selectedCustomer}
                                        onChange={(value) => setSelectedCustomer(value)}
                                        options={customerAllData.map((customer: any) => ({
                                            label: customer.text,
                                            value: customer.value,
                                        }))}
                                        showSearch
                                        allowClear
                                        optionFilterProp="label"
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    />
                                </div>

                                <div className="col-md-5">
                                    <button
                                        className="submit-btn w-auto"
                                        onClick={handleAssignToCustomer}
                                        disabled={selectedRows.length === 0 || !selectedCustomer || assignCustomerLoading}
                                    >
                                        {assignCustomerLoading ? "Assigning..." : "Assign To Customer"}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <DataGrid
                        ref={dataGridRef}
                        dataSource={data}
                        showBorders
                        showRowLines
                        rowAlternationEnabled
                        columnAutoWidth={true}
                        keyExpr="id"
                        onSelectionChanged={(e) => {
                            const validSelectedIds = e.selectedRowsData
                                .filter((item: any) => !item.isDisabled)
                                .map((item: any) => item.id);
                            setSelectedRows(validSelectedIds);
                        }}
                        onRowPrepared={(e) => {
                            if (e.rowType === 'data' && e.data?.isDisabled) {
                                e.rowElement.classList.add('dx-row-disabled');
                            }
                        }}
                        onCellPrepared={(e: any) => {
                            if (e.rowType === 'data' && e.column.command === 'select' && e.data?.isDisabled) {
                                e.cellElement.innerHTML = '';
                            }
                        }}
                    >
                        {selectedId && !selectedId.isAllStore && (
                            <Selection mode="multiple" showCheckBoxesMode="always" />
                        )}
                        <FilterRow visible />
                        <HeaderFilter visible />
                        <Paging defaultPageSize={10} />
                        <Pager
                            visible={true}
                            allowedPageSizes={[10, 25, 50, 100]}
                            showPageSizeSelector={true}
                            showNavigationButtons={true}
                            displayMode="full"
                            showInfo={true}
                        />
                        <Column dataField="voucherCode" caption="Voucher Code" width='20%' />
                        <Column dataField="storeName" caption="Store" />
                        <Column dataField="customerName" caption="Customer Name" />
                        <Column
                            dataField="fromDate"
                            dataType='date'
                            caption="From Date"
                            format="dd/MM/yyyy"
                            width='20%'
                        />
                        <Column
                            dataField="toDate"
                            caption="To Date"
                            dataType='date'
                            format="dd/MM/yyyy"
                            width='20%'
                        />
                    </DataGrid>
                </Modal.Body>
                <Modal.Footer>

                    <button className='add-btn' onClick={handleClose}>
                        Cancel
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ViewModalWithTable;
